/* Reusable component that renders one tool doc.
 * Used both by /docs/:toolId page AND by the in-wizard help slide-over.
 */
import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { CHART_MAP } from "../docs/charts.jsx";

export default function DocBody({ doc, toolId, compact = false }) {
  if (!doc) {
    return (
      <div className="py-12 text-center text-sage-500 text-sm">
        Documentation for this tool is being written. Check back soon.
      </div>
    );
  }
  const Icon = Icons[doc.icon] || Icons.BookOpen;
  const Chart = doc.chart ? CHART_MAP[doc.chart] : null;
  const padding = compact ? "px-1" : "";

  return (
    <article className={`prose-sage max-w-none ${padding}`}>
      <header className="border-b border-sage-100 pb-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-sage-600 text-white rounded-md flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500">{doc.category}</p>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-sage-900 leading-tight">{doc.name}</h1>
            <p className="text-sage-600 text-sm mt-1 italic">{doc.tagline}</p>
          </div>
        </div>
      </header>

      <Section title="Purpose">
        <p className="text-sage-700 text-sm leading-relaxed">{doc.purpose}</p>
      </Section>

      {Chart && (
        <div className="my-5 rounded-md border border-sage-100 bg-cream/40 p-4">
          <Chart />
        </div>
      )}

      <Section title="History">
        <p className="text-sage-700 text-sm leading-relaxed">{doc.history}</p>
      </Section>

      <Section title="When to Use">
        <ul className="text-sage-700 text-sm space-y-1.5 list-disc pl-5">
          {doc.whenToUse.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      </Section>

      <Section title="How to Use">
        <ol className="text-sage-700 text-sm space-y-2 list-decimal pl-5 marker:text-sage-500 marker:font-semibold">
          {doc.howToUse.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
      </Section>

      <Section title="Worked Example">
        <div className="bg-sage-50 border-l-2 border-sage-400 pl-4 py-2 text-sage-700 text-sm leading-relaxed italic">
          {doc.example}
        </div>
      </Section>

      <Section title="Outputs & Inferences">
        <ul className="text-sage-700 text-sm space-y-1.5 list-disc pl-5">
          {doc.outputs.map((o, i) => <li key={i}>{o}</li>)}
        </ul>
      </Section>

      <Section title="Common Pitfalls">
        <ul className="text-sage-700 text-sm space-y-1.5 list-disc pl-5">
          {doc.pitfalls.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </Section>

      {doc.furtherReading && doc.furtherReading.length > 0 && (
        <Section title="Further Reading">
          <ul className="text-sage-700 text-sm space-y-1">
            {doc.furtherReading.map((r, i) => (
              <li key={i}>
                <span className="font-medium">{r.label}</span>
                <span className="text-sage-500"> — {r.ref}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {!compact && toolId && (
        <div className="mt-8 pt-4 border-t border-sage-100 flex flex-wrap items-center justify-between gap-3">
          <Link to="/docs" className="text-sm text-sage-500 hover:text-sage-700 inline-flex items-center gap-1">
            <Icons.ArrowLeft className="w-4 h-4" /> Back to docs index
          </Link>
          <Link to="/catalogue" className="text-sm bg-sage-600 text-white rounded-md px-4 py-2 font-semibold hover:bg-sage-700 transition-colors inline-flex items-center gap-1.5">
            <Icons.Wrench className="w-4 h-4" /> Open the {doc.name} tool
          </Link>
        </div>
      )}
    </article>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-5">
      <h2 className="font-heading text-xs font-bold uppercase tracking-wider text-sage-500 mb-2">{title}</h2>
      {children}
    </section>
  );
}
